import { FC, Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { ResourceExplorerBudgetProgress } from "../ResourceExplorerBudgetProgress";
import { ResourceExplorerBudgetTooltip } from "../tooltip/ResourceExplorerBudgetTooltip";
import { money } from "@/utils/numeral/money";
import { Budget } from "@/services/cloudchipr.api";
import { getPlannedBudgetAmount } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/getPlannedBudgetAmount";
import { useAppSelector } from "@/store/hooks";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { calculateBudgetProgress } from "@/components/pages/budgets-and-alerts/utils/helpers/calculateBudgetProgress";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { isBudgetUsedDataNoCollected } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/isBudgetUsedDataNoCollected";

interface ResourceExplorerBudgetOptionProps {
  budget: Budget;
}

export const ResourceExplorerBudgetOption: FC<
  ResourceExplorerBudgetOptionProps
> = ({ budget }) => {
  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );

  const budgetAmount =
    budget?.type === "fixed"
      ? budget?.amount
      : budget &&
        getPlannedBudgetAmount(
          budget.start_date,
          budget.period,
          budget.planned_amounts,
          quarterStartMonth,
        );

  const noData = isBudgetUsedDataNoCollected(
    budget?.used ?? 0,
    budget?.start_date,
    budget?.period,
  );

  const progress = calculateBudgetProgress(budget?.used, budgetAmount);

  return (
    <ResourceExplorerBudgetTooltip
      name={budget.name}
      amount={budgetAmount ?? 0}
      used={budget.used}
      progress={progress}
      startDate={budget.start_date}
      period={budget.period}
      hideBudgetDateLabel
    >
      <Box py={0.5} minWidth={200}>
        <TypographyWithTooltip
          variant="subtitle2"
          fontWeight="regular"
          title={budget.name}
        />

        <Fragment>
          <Typography variant="body2" color="text.secondary">
            {money(budgetAmount)}
          </Typography>

          <ResourceExplorerBudgetProgress
            noData={noData}
            progress={progress}
            startDate={budget.start_date}
          />
        </Fragment>
      </Box>
    </ResourceExplorerBudgetTooltip>
  );
};
